/**
 * Shared utility for creating mention notifications.
 * Used by LeadComments and PDF annotations.
 */

import { db } from './firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

export interface CreateMentionNotificationParams {
  leadId: string;
  leadName: string;
  mentionedUserId: string;
  mentionedUserName: string;
  fromUserId: string;
  fromUserName: string;
  /** Lead team-note id, so the bell can open that comment. */
  commentId?: string;
}

export async function createMentionNotification(params: CreateMentionNotificationParams): Promise<void> {
  const { leadId, leadName, mentionedUserId, mentionedUserName: _mentionedUserName, fromUserId, fromUserName, commentId } = params;
  const sourceId = commentId?.trim() || '';
  try {
    await addDoc(collection(db, 'notifications'), {
      type: 'mention',
      userId: mentionedUserId,
      fromUserId,
      fromUserName,
      leadId,
      leadName: leadName || 'a lead',
      message: `${fromUserName} mentioned you in a comment`,
      read: false,
      ...(sourceId ? { sourceId } : {}),
      createdAt: Timestamp.now(),
    });

    await addDoc(collection(db, 'userNotifications'), {
      userId: mentionedUserId,
      type: 'mention',
      title: 'You were mentioned',
      message: `${fromUserName} mentioned you on ${leadName || 'a lead'}`,
      read: false,
      leadId,
      ...(sourceId ? { sourceId } : {}),
      phoneNumber: null,
      isPriority: false,
      projectType: null,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('[createMentionNotification] Error:', error);
  }
}

export interface CreateTaskMentionNotificationParams {
  taskId: string;
  taskTitle: string;
  projectId?: string;
  mentionedUserId: string;
  fromUserId: string;
  fromUserName: string;
}

/**
 * Client-side task @mention notifications. Prefer relying on the
 * `onTaskCommentCreated` Cloud Function, which also creates `task_mention`
 * rows and avoids duplicate bell entries when both run.
 */
export async function createTaskMentionNotification(params: CreateTaskMentionNotificationParams): Promise<void> {
  const { taskId, taskTitle, projectId, mentionedUserId, fromUserId, fromUserName } = params;
  try {
    await addDoc(collection(db, 'userNotifications'), {
      userId: mentionedUserId,
      type: 'task_mention',
      title: 'You were mentioned on a task',
      message: `${fromUserName} mentioned you on "${taskTitle}"`,
      read: false,
      taskId,
      projectId: projectId || null,
      isPriority: false,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('[createTaskMentionNotification] Error:', error);
  }
}

export interface NotifyBoardMentionsParams {
  mentionedUserIds: string[];
  fromUserId: string;
  fromUserName: string;
  leadId: string;
  dealId: string;
  projectId?: string;
  projectName: string;
  nodeId: string;
}

/** Client-side board-note @mentions. Pin-comment mentions are dispatched by the Cloud Function. */
export async function notifyBoardMentions(params: NotifyBoardMentionsParams): Promise<void> {
  const unique = Array.from(new Set(params.mentionedUserIds.filter((id) => id && id !== params.fromUserId)));
  if (unique.length === 0) return;
  await Promise.all(
    unique.map((mentionedUserId) =>
      addDoc(collection(db, 'userNotifications'), {
        userId: mentionedUserId,
        type: 'board_mention',
        title: 'You were mentioned on the inspiration board',
        message: `${params.fromUserName} mentioned you on "${params.projectName}"`,
        read: false,
        leadId: params.leadId,
        dealId: params.dealId,
        projectId: params.projectId || null,
        sourceId: params.nodeId,
        isPriority: false,
        createdAt: Timestamp.now(),
      }).catch((error) => {
        console.error('[notifyBoardMentions] Error:', error);
      }),
    ),
  );
}
